import { API_ENDPOINTS, httpClientInstance } from "@/api"
import type {
  Department,
  DepartmentCreateRequest,
  DepartmentFormData,
  DepartmentSettingsFormData,
  DepartmentDeactivationImpact,
  DepartmentDeactivationResult,
  DepartmentUnassignConflict,
  DepartmentUpdateRequest,
  InstanceAction,
} from "@/types/department.types"

/** Error thrown by delete/deactivate when the department still has members or
 * instances and `confirm` wasn't sent — carries the dry-run counts so the UI
 * can render a proper confirmation with an instance_action choice. */
export interface DepartmentUnassignConflictError extends Error {
  conflict: DepartmentUnassignConflict
}

/** Error thrown by deactivate when the department has anything to freeze and
 * `confirm` wasn't sent — carries the impact counts for the confirmation. */
export interface DepartmentDeactivationConflictError extends Error {
  impact: DepartmentDeactivationImpact
}

export interface DepartmentUnassignResult {
  success: boolean
  message: string
  members_unassigned: number
  instances_unassigned: number
}

export interface DepartmentActivationResult {
  success: boolean
  message: string
  department_id: number
  department_name: string
}

/**
 * Departments Service
 *
 * Handles department management CRUD operations including:
 * - Fetching all departments
 * - Creating new departments
 * - Fetching single department details
 * - Updating existing departments
 * - Deleting departments
 */
export class DepartmentsService {
  /**
   * Format API error response into user-friendly message
   * @param errorData Error data from API response
   */
  private formatErrorMessage(errorData: any): string {
    // If error data is a string, guard against raw HTML (e.g. Django debug pages)
    if (typeof errorData === "string") {
      if (errorData.trim().startsWith("<")) {
        return "An unexpected server error occurred. Please try again."
      }
      return errorData
    }

    // If error has a detail field (common Django REST framework format)
    if (errorData?.detail) {
      return errorData.detail
    }

    // Department action errors (activate/deactivate/destroy) use {error, message}
    if (typeof errorData?.message === "string") {
      return errorData.message
    }

    // If error data is an object with field-specific errors
    if (typeof errorData === "object" && errorData !== null) {
      const errors: string[] = []

      // Iterate through each field and get the first error message
      for (const [field, messages] of Object.entries(errorData)) {
        if (Array.isArray(messages) && messages.length > 0) {
          // Format: "Field: Error message"
          const formattedField = field.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
          errors.push(`${formattedField}: ${messages[0]}`)
        }
      }

      if (errors.length > 0) {
        return errors.join("\n")
      }
    }

    return "Operation failed. Please try again."
  }

  /**
   * Build the error thrown by delete/deactivate. A 409 with unassign counts
   * becomes a typed DepartmentUnassignConflictError; anything else is a
   * regular Error carrying fieldErrors, matching the rest of this service.
   */
  private buildActionError(error: any): Error {
    const errorData = error.response?.data
    if (error.response?.status === 409 && typeof errorData?.ssh_rules_count === "number") {
      const impactError = new Error(errorData.message || "Confirmation required") as DepartmentDeactivationConflictError
      impactError.impact = {
        members_count: errorData.members_count,
        instances_count: errorData.instances_count,
        ssh_rules_count: errorData.ssh_rules_count,
        alerts_count: errorData.alerts_count,
      }
      return impactError
    }
    if (error.response?.status === 409 && typeof errorData?.members_count === "number") {
      const conflictError = new Error(errorData.message || "Confirmation required") as DepartmentUnassignConflictError
      conflictError.conflict = {
        members_count: errorData.members_count,
        instances_count: errorData.instances_count,
      }
      return conflictError
    }
    const message = this.formatErrorMessage(errorData)
    const apiError = new Error(message)
    ;(apiError as any).fieldErrors = errorData
    return apiError
  }

  /**
   * Transform form data to API request format
   * @param formData Department form data from UI
   */
  private convertToCreatePayload(formData: DepartmentFormData): DepartmentCreateRequest {
    return {
      name: formData.name,
      description: formData.description,
      admin: formData.admin,
      cost_threshold: formData.cost_threshold || "",
      is_active: formData.is_active,
      email_notifs: formData.email_notifs,
      sms_notifs: formData.sms_notifs,
      auto_shutdown_server: formData.auto_shutdown_server,
      city: formData.city,
      state: formData.state,
    }
  }

  /**
   * Transform form data to API update request format
   * @param formData Department form data from UI
   */
  private convertToUpdatePayload(formData: DepartmentFormData): DepartmentUpdateRequest {
    return {
      name: formData.name,
      description: formData.description,
      admin: formData.admin,
      cost_threshold: formData.cost_threshold || "",
      is_active: formData.is_active,
      email_notifs: formData.email_notifs,
      sms_notifs: formData.sms_notifs,
      auto_shutdown_server: formData.auto_shutdown_server,
      city: formData.city,
      state: formData.state,
    }
  }

  /**
   * Settings-only update of a department (PATCH). Sends only the settings keys, never
   * `admin` or `is_active`, so a department admin can use it on their own department.
   * PATCH /members/departments/{id}/patch/
   */
  async updateDepartmentSettings(id: number, settings: DepartmentSettingsFormData): Promise<Department> {
    try {
      const payload = {
        name: settings.name,
        description: settings.description,
        cost_threshold: settings.cost_threshold || "",
        email_notifs: settings.email_notifs,
        sms_notifs: settings.sms_notifs,
        auto_shutdown_server: settings.auto_shutdown_server,
        city: settings.city,
        state: settings.state,
      }
      return await httpClientInstance.patch<Department>(API_ENDPOINTS.members.departments.patch(id), payload)
    } catch (error: unknown) {
      const errorData = (error as { response?: { data?: unknown } }).response?.data
      const message = this.formatErrorMessage(errorData)
      const apiError = new Error(message)
      Object.assign(apiError, { fieldErrors: errorData })
      throw apiError
    }
  }

  /**
   * Get all departments
   * GET /members/departments/list/
   */
  async getAllDepartments(): Promise<Department[]> {
    try {
      const response = await httpClientInstance.get<Department[]>(API_ENDPOINTS.members.departments.list)
      return response
    } catch (error: any) {
      const errorData = error.response?.data
      const message = this.formatErrorMessage(errorData)
      const apiError = new Error(message)
      ;(apiError as any).fieldErrors = errorData
      throw apiError
    }
  }

  /**
   * Get a specific department by ID
   * GET /members/departments/{id}/retrieve/
   * @param id Department ID
   */
  async getDepartmentById(id: number): Promise<Department> {
    try {
      const response = await httpClientInstance.get<Department>(API_ENDPOINTS.members.departments.retrieve(id))
      return response
    } catch (error: any) {
      const errorData = error.response?.data
      const message = this.formatErrorMessage(errorData)
      const apiError = new Error(message)
      ;(apiError as any).fieldErrors = errorData
      throw apiError
    }
  }

  /**
   * Create a new department
   * POST /members/departments/create/
   * @param formData Department form data
   */
  async createDepartment(formData: DepartmentFormData): Promise<Department> {
    try {
      const requestData = this.convertToCreatePayload(formData)
      const response = await httpClientInstance.post<Department>(API_ENDPOINTS.members.departments.create, requestData)
      return response
    } catch (error: any) {
      const errorData = error.response?.data
      const message = this.formatErrorMessage(errorData)
      const apiError = new Error(message)
      ;(apiError as any).fieldErrors = errorData
      throw apiError
    }
  }

  /**
   * Update an existing department
   * PUT /members/departments/{id}/update/
   * @param id Department ID
   * @param formData Department form data
   */
  async updateDepartment(id: number, formData: DepartmentFormData): Promise<Department> {
    try {
      const requestData = this.convertToUpdatePayload(formData)
      const response = await httpClientInstance.put<Department>(
        API_ENDPOINTS.members.departments.update(id),
        requestData
      )
      return response
    } catch (error: any) {
      const errorData = error.response?.data
      const message = this.formatErrorMessage(errorData)
      const apiError = new Error(message)
      ;(apiError as any).fieldErrors = errorData
      throw apiError
    }
  }

  /**
   * Delete a department. Bulk-unassigns members, the admin, and EC2
   * resources first. Without `confirm: true`, a populated department throws
   * a DepartmentUnassignConflictError (409) instead of deleting anything.
   * DELETE /members/departments/{id}/delete/
   * @param id Department ID
   */
  async deleteDepartment(
    id: number,
    confirm = false,
    instanceAction: InstanceAction = "leave"
  ): Promise<DepartmentUnassignResult> {
    try {
      return await httpClientInstance.delete<DepartmentUnassignResult>(API_ENDPOINTS.members.departments.delete(id), {
        confirm,
        instance_action: instanceAction,
      })
    } catch (error: any) {
      throw this.buildActionError(error)
    }
  }

  /**
   * Deactivate (freeze) a department. Members, the admin and instances stay in
   * place; SSH rules are revoked, alerts disabled, members become view-only.
   * Without `confirm: true`, a non-empty department throws a
   * DepartmentDeactivationConflictError (409) with the impact counts.
   * POST /members/departments/{id}/deactivate/
   * @param id Department ID
   */
  async deactivateDepartment(id: number, confirm = false): Promise<DepartmentDeactivationResult> {
    try {
      return await httpClientInstance.post<DepartmentDeactivationResult>(
        API_ENDPOINTS.members.departments.deactivate(id),
        { confirm }
      )
    } catch (error: any) {
      throw this.buildActionError(error)
    }
  }

  /**
   * Activate a department. Requires an assigned admin (400 otherwise).
   * POST /members/departments/{id}/activate/
   * @param id Department ID
   */
  async activateDepartment(id: number): Promise<DepartmentActivationResult> {
    try {
      return await httpClientInstance.post<DepartmentActivationResult>(API_ENDPOINTS.members.departments.activate(id))
    } catch (error: any) {
      const errorData = error.response?.data
      const message = this.formatErrorMessage(errorData)
      const apiError = new Error(message)
      ;(apiError as any).fieldErrors = errorData
      throw apiError
    }
  }

  /**
   * Assign admin to a department
   * POST /members/departments/{id}/assign-admin/
   * @param departmentId Department ID
   * @param memberId Member ID to assign as admin
   */
  async assignAdmin(departmentId: number, memberId: number): Promise<Department> {
    try {
      const response = await httpClientInstance.post<{
        status: string
        message: string
        department: Department
        admin: any
      }>(API_ENDPOINTS.members.departments.assignAdmin(departmentId), { sub_account_id: memberId })
      // Extract department from nested response
      return response.department
    } catch (error: any) {
      const errorData = error.response?.data
      const message = this.formatErrorMessage(errorData)
      const apiError = new Error(message)
      ;(apiError as any).fieldErrors = errorData
      throw apiError
    }
  }

  /**
   * Remove admin from a department
   * POST /members/departments/{id}/remove-admin/
   * @param departmentId Department ID
   * @param adminId Current admin ID to remove
   */
  async removeAdmin(departmentId: number, adminId: number): Promise<Department> {
    try {
      const response = await httpClientInstance.post<{
        status: string
        message: string
        department: Department
      }>(API_ENDPOINTS.members.departments.removeAdmin(departmentId), { admin: adminId })
      // Extract department from nested response
      return response.department
    } catch (error: any) {
      const errorData = error.response?.data
      const message = this.formatErrorMessage(errorData)
      const apiError = new Error(message)
      ;(apiError as any).fieldErrors = errorData
      throw apiError
    }
  }
}

/**
 * Singleton instance of DepartmentsService
 */
export const departmentsService = new DepartmentsService()

/**
 * Get all departments
 */
export const getAllDepartments = async (): Promise<Department[]> => {
  return departmentsService.getAllDepartments()
}

/**
 * Get a specific department by ID
 * @param id Department ID
 */
export const getDepartmentById = async (id: number): Promise<Department> => {
  return departmentsService.getDepartmentById(id)
}

/**
 * Create a new department
 * @param formData Department form data
 */
export const createDepartment = async (formData: DepartmentFormData): Promise<Department> => {
  return departmentsService.createDepartment(formData)
}

/**
 * Update an existing department
 * @param id Department ID
 * @param formData Department form data
 */
export const updateDepartment = async (id: number, formData: DepartmentFormData): Promise<Department> => {
  return departmentsService.updateDepartment(id, formData)
}

/**
 * Delete a department
 * @param id Department ID
 */
export const deleteDepartment = async (
  id: number,
  confirm = false,
  instanceAction: InstanceAction = "leave"
): Promise<DepartmentUnassignResult> => {
  return departmentsService.deleteDepartment(id, confirm, instanceAction)
}

/**
 * Deactivate a department
 * @param id Department ID
 */
export const deactivateDepartment = async (id: number, confirm = false): Promise<DepartmentDeactivationResult> => {
  return departmentsService.deactivateDepartment(id, confirm)
}

/**
 * Activate a department
 * @param id Department ID
 */
export const activateDepartment = async (id: number): Promise<DepartmentActivationResult> => {
  return departmentsService.activateDepartment(id)
}

/**
 * Assign admin to a department
 * @param departmentId Department ID
 * @param memberId Member ID to assign as admin
 */
export const assignAdmin = async (departmentId: number, memberId: number): Promise<Department> => {
  return departmentsService.assignAdmin(departmentId, memberId)
}

/**
 * Remove admin from a department
 * @param departmentId Department ID
 * @param adminId Current admin ID to remove
 */
export const removeAdmin = async (departmentId: number, adminId: number): Promise<Department> => {
  return departmentsService.removeAdmin(departmentId, adminId)
}
